import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography
} from '@mui/material';
import { Group, Person } from '@mui/icons-material';
import { COLORS } from '../../shared/colors';
import { cleanDeletedEmail } from '../../shared/commonFunctions';
import { PROJECT_TYPES } from '../../shared/appConstants';
import './TeamMembersDialog.css';

const memberModalColors = COLORS.projectManagement.membersModal;

const dialogStyles = {
  headerAvatar: { bgcolor: memberModalColors.headerAvatarBg, width: 48, height: 48 },
  headerIcon: { fontSize: 28 },
  memberCard: (roleType) => ({
    mb: 1.5,
    border: roleType === 'LEAD' ? `2px solid ${memberModalColors.leadBlue}` : 
            roleType === 'SCRUM_MASTER' ? `2px solid ${memberModalColors.scrumMasterBorder}` : 
            roleType === 'PO' ? `2px solid ${memberModalColors.poBorder}` : 
            `1px solid ${COLORS.gray[300]}`,
    bgcolor: roleType === 'LEAD' ? memberModalColors.leadRowBg : 
             roleType === 'SCRUM_MASTER' ? memberModalColors.scrumMasterRowBg : 
             roleType === 'PO' ? memberModalColors.poRowBg : 
             COLORS.white
  }),
  memberAvatar: (roleType) => ({
    bgcolor: roleType === 'LEAD' ? memberModalColors.leadBlue : 
             roleType === 'SCRUM_MASTER' ? memberModalColors.scrumMasterBorder : 
             roleType === 'PO' ? memberModalColors.poBorder : 
             memberModalColors.memberAvatarBg,
    width: 40,
    height: 40,
    fontWeight: 700
  }),
  leadChip: {
    bgcolor: memberModalColors.leadChipBg,
    color: COLORS.white,
    fontWeight: 600,
    fontSize: '0.72rem'
  },
  scrumMasterChip: {
    bgcolor: memberModalColors.scrumMasterChipBg,
    color: COLORS.white,
    fontWeight: 600,
    fontSize: '0.72rem'
  },
  poChip: {
    bgcolor: memberModalColors.poChipBg,
    color: COLORS.white,
    fontWeight: 600,
    fontSize: '0.72rem'
  },
  membersCountNumber: { fontWeight: 700, color: memberModalColors.leadBlue }
};

const getInitials = (name = '') => {
  const nameParts = name.split(' ').filter(Boolean);
  if (nameParts.length >= 2) {
    return `${nameParts[0]?.[0] || ''}${nameParts[nameParts.length - 1]?.[0] || ''}`.toUpperCase();
  }
  return (nameParts[0]?.[0] || '').toUpperCase();
};

const getStatusLabel = (status, labels) => {
  const normalizedStatus = (status || '').trim();
  if (!normalizedStatus) return labels.unknownStatus || labels.active;
  if (normalizedStatus === 'SOFT-DELETE') return 'Deleted';
  return normalizedStatus;
};

const getStatusChipSx = (status) => {
  const normalizedStatus = (status || '').trim().toUpperCase();

  if (normalizedStatus === 'ACTIVE') {
    return {
      bgcolor: COLORS.white,
      color: memberModalColors.activeChipText,
      border: `1px solid ${memberModalColors.activeChipBorder}`,
      fontWeight: 500,
      fontSize: '0.72rem'
    };
  }

  if (normalizedStatus === 'INACTIVE') {
    return {
      bgcolor: COLORS.gray[100],
      color: COLORS.gray[700],
      border: `1px solid ${COLORS.gray[300]}`,
      fontWeight: 500,
      fontSize: '0.72rem'
    };
  }

  if (normalizedStatus === 'SOFT-DELETE') {
    return {
      bgcolor: '#FDECEC',
      color: '#D92D20',
      border: '1px solid #D92D20',
      fontWeight: 500,
      fontSize: '0.72rem'
    };
  }

  return {
    bgcolor: COLORS.gray[50],
    color: COLORS.gray[700],
    border: `1px solid ${COLORS.gray[300]}`,
    fontWeight: 500,
    fontSize: '0.72rem'
  };
};

const TeamMembersDialog = ({
  open,
  onClose,
  title,
  subtitle,
  members = [],
  leadName,
  projectType,
  scrumMasterId,
  productOwnerIds = [],
  labels
}) => {
  const safeMembers = Array.isArray(members) ? members : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: 'team-members-paper',
        sx: {
          '--team-members-title-text': memberModalColors.titleText,
          '--team-members-section-title-text': memberModalColors.sectionTitleText,
          '--team-members-scrollbar-track': memberModalColors.scrollbarTrack,
          '--team-members-scrollbar-thumb': memberModalColors.scrollbarThumb,
          '--team-members-card-shadow': memberModalColors.memberCardShadow,
          '--team-members-footer-bg': memberModalColors.footerBg,
          '--team-members-close-border': memberModalColors.closeButtonBorder,
          '--team-members-close-text': memberModalColors.closeButtonText,
          '--team-members-close-hover-bg': memberModalColors.closeButtonHoverBg
        }
      }}
    >
      <DialogTitle className="team-members-dialog-title">
        <Box className="team-members-dialog-header">
          <Box className="team-members-dialog-heading">
            <Avatar sx={dialogStyles.headerAvatar}>
              <Group sx={dialogStyles.headerIcon} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="700" color="text.primary">
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            </Box>
          </Box>
          <Box className="team-members-count-box">
            <Typography variant="h6" sx={dialogStyles.membersCountNumber}>
              {safeMembers.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {labels.totalMembers}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent className="team-members-dialog-content">
        <Typography className="team-members-section-title">
          <Person fontSize="small" /> {labels.teamMembers}
        </Typography>
        <Box className="team-members-list">
          {safeMembers.map((member, index) => {
            const memberName = member?.name || labels.unknownUser;
            const isScrum = (projectType || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase();
            const isLead = !isScrum && memberName === leadName;
            const isScrumMaster = isScrum && member?.id === scrumMasterId;
            const isProductOwner = isScrum && Array.isArray(productOwnerIds) && productOwnerIds.includes(member?.id);

            let roleType = 'MEMBER';
            if (isLead) roleType = 'LEAD';
            else if (isScrumMaster) roleType = 'SCRUM_MASTER';
            else if (isProductOwner) roleType = 'PO';

            return (
              <Card
                key={member?.id ?? member?.email ?? `member-${index}`}
                elevation={0}
                className="team-member-card"
                sx={dialogStyles.memberCard(roleType)}
              >
                <Box className="team-member-row">
                  <Box className="team-member-identity">
                    <Avatar sx={dialogStyles.memberAvatar(roleType)}>
                      {getInitials(memberName)}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight="600" color="text.primary">
                        {memberName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {cleanDeletedEmail(member?.email)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box className="team-member-chips">
                    {isLead && (
                      <Chip
                        label={labels.lead}
                        size="small"
                        sx={dialogStyles.leadChip}
                      />
                    )}
                    {isScrumMaster && (
                      <Chip
                        label={labels.scrumMaster || 'Scrum Master'}
                        size="small"
                        sx={dialogStyles.scrumMasterChip}
                      />
                    )}
                    {isProductOwner && (
                      <Chip
                        label={labels.productOwner || 'Product Owner'}
                        size="small"
                        sx={dialogStyles.poChip}
                      />
                    )}
                    <Chip
                      label={getStatusLabel(member?.status, labels)}
                      size="small"
                      sx={getStatusChipSx(member?.status)}
                    />
                  </Box>
                </Box>
              </Card>
            );
          })}
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions className="team-members-actions">
        <Button onClick={onClose} variant="outlined" className="team-members-close">
          {labels.close}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamMembersDialog;
